import type {
  ImportCommitResponseDto,
  ImportJobResponseDto,
  RollbackResponseDto,
} from '@trellis/api'
import type { RequestResult } from '@trellis/shared'
import { toast } from 'sonner'

import { env } from '@/env'
import { apiClient } from '@/lib/api-client'
import { downloadAuthenticatedFile } from '@/lib/download-file'
import { request } from '@/lib/request'
import { getStoredTenantSlug } from '@/lib/tenant'

export type ImportType = 'CLIENTS' | 'INVOICES' | 'LINE_ITEMS' | 'QUOTES' | 'EXPENSES'
export type TemplateType = 'clients' | 'invoices' | 'line-items' | 'quotes' | 'expenses'

export async function uploadCsv(
  file: File,
  type: ImportType,
): Promise<RequestResult<ImportJobResponseDto>> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('type', type)
  try {
    const tenantSlug = getStoredTenantSlug()
    const response = await fetch(`${env.VITE_API_BASE_URL}/api/imports/upload`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
      headers: tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : undefined,
    })
    const json = (await response.json()) as {
      data?: ImportJobResponseDto
      code?: string
      message?: string
    }
    if (!response.ok) {
      return {
        status: response.status,
        data: null,
        pagination: null,
        error: {
          code: json.code ?? 'UPLOAD_ERROR',
          message: json.message ?? 'Upload failed',
        },
      }
    }
    return { status: response.status, data: json.data!, pagination: null, error: null }
  } catch (caughtError) {
    return {
      status: 500,
      data: null,
      pagination: null,
      error: {
        code: 'NETWORK_ERROR',
        message:
          caughtError instanceof Error
            ? caughtError.message
            : 'An unexpected error occurred',
      },
    }
  }
}

export const listImports = () =>
  request<ImportJobResponseDto[]>(() => apiClient.api.dataImportControllerListImports())

export const getImport = (id: string) =>
  request<ImportJobResponseDto>(() => apiClient.api.dataImportControllerGetImport({ id }))

export const commitImport = (id: string) =>
  request<ImportCommitResponseDto>(() =>
    apiClient.api.dataImportControllerCommitImport({ id }),
  )

export const rollbackImport = (id: string) =>
  request<RollbackResponseDto>(() =>
    apiClient.api.dataImportControllerRollbackImport({ id }),
  )

export const deleteImport = (id: string) =>
  request<void>(() => apiClient.api.dataImportControllerDeleteImport({ id }))

export function downloadTemplate(type: TemplateType): void {
  void downloadAuthenticatedFile(
    `${env.VITE_API_BASE_URL}/api/imports/templates/${type}`,
    `${type}-template.csv`,
  ).catch((caughtError: unknown) => {
    const message =
      caughtError instanceof Error ? caughtError.message : 'Failed to download template'
    toast.error('Download failed', { description: message })
  })
}
